// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';

import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { PIPELINE_META, statusMeta } from '@/components/greenlight/appStatusMeta';
import { IntegrationStack } from '@/components/greenlight/IntegrationStack';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { OwnerAvatar } from '@/components/greenlight/OwnerAvatar';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { formatCountCompact } from '@/components/greenlight/formatCount';
import { LastUsedSignal, ScheduledToPauseBadge } from './lastUsed';
import { type AppListItem, type AppUsageSummaryItem } from '@greenlight/shared';

interface AppListCardProps {
  app: AppListItem;
  isIT: boolean;
  showIntegrations: boolean;
  usage?: AppUsageSummaryItem;
  usageWindowDays?: number;
}

// Mobile card renderer for the apps list — matches the mobile design. The whole
// card is made the tap target by DataList; this only renders the body.
export function AppListCard({
  app,
  isIT,
  showIntegrations,
  usage,
  usageWindowDays,
}: AppListCardProps): React.JSX.Element {
  const meta = statusMeta(app.status);
  const pipelineMeta = app.last_pipeline_status
    ? (PIPELINE_META[app.last_pipeline_status] ?? {
        variant: 'inactive' as StatusBadgeVariant,
        label: app.last_pipeline_status,
      })
    : null;

  return (
    <div className="-mx-4 -my-4 rounded-[inherit] px-4 py-4">
      {/* Top row: icon + name/slug left, status badge right */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <AppIconTile iconUrl={app.icon_url} name={app.name} slug={app.slug} size={40} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex items-center gap-1.5 text-[15px] font-semibold text-foreground">
              <span className="truncate">{app.name}</span>
            </span>
            <MonoValue className="w-fit text-[11px]">{app.slug}</MonoValue>
          </div>
        </div>
        <div className="mt-0.5 flex shrink-0 flex-col items-end gap-1">
          <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
          {app.scheduled_to_pause && <ScheduledToPauseBadge />}
        </div>
      </div>

      {/* Label / value rows */}
      <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5">
        {app.owner_email && (
          <>
            <dt className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Owner
            </dt>
            <dd className="flex min-w-0 items-center gap-1.5">
              <OwnerAvatar name={app.owner_name} email={app.owner_email} />
              <span className="min-w-0 truncate text-sm text-foreground">{app.owner_email}</span>
            </dd>
          </>
        )}
        {showIntegrations && (app.integrations?.length ?? 0) > 0 && (
          <>
            <dt className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Integrations
            </dt>
            <dd>
              <IntegrationStack slugs={app.integrations ?? []} />
            </dd>
          </>
        )}
        {isIT && usage && (
          <>
            <dt className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Usage ({usageWindowDays ?? 7}d)
            </dt>
            <dd className="text-sm tabular-nums text-muted-foreground">
              {formatCountCompact(usage.active_users)}{' '}
              {usage.active_users === 1 ? 'person' : 'people'}
            </dd>
            <dt className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Last used
            </dt>
            <dd className="text-sm">
              <LastUsedSignal usage={usage} />
            </dd>
          </>
        )}
        {(app.last_deploy_at || pipelineMeta) && (
          <>
            <dt className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Last updated
            </dt>
            <dd className="flex flex-wrap items-center gap-2">
              {pipelineMeta && (
                <StatusBadge variant={pipelineMeta.variant}>{pipelineMeta.label}</StatusBadge>
              )}
              {app.last_deploy_at && (
                <span className="text-sm text-muted-foreground">
                  {relativeTime(app.last_deploy_at)}
                </span>
              )}
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}
